// gate-notes.ts — (TF-6b-4) the gate notes' words, pure: one line per check, the screenplay's lines, and what is said when
// the engine's reads aren't there yet (planning 10-17 11:25).
import type { GateNotes } from "@vms/contracts/browser";
export interface GateCheckView { stepId: string; beatId: string | null; status: string; score: number | null; passed: boolean | null; reasons: string[] | null;
  /** (zip 48) a grader shown but never judged by */ advisory?: boolean }
export interface GateNotesView {
  gate: { stepId: string; name: string; holdsAt: string | null; onReject: string | null;
    /** (tf3-37; zip 51) the gate's own note, as the engine cleaned it — the storyboard's unvoiced lines, "Staging (advisory): …" */
    note: string | null } | null;
  checks: GateCheckView[]; reasons: "engine" | "not_readable";
  /** (13:40) C2.0's card, contracts' GateNotes type; absent until C2.0 */ rulesCard: NonNullable<GateNotes["rulesCard"]> | null;
}
/** (relay 12:30) the backend's screenplay answer: the workers' beats in index order, or where to read it while they can't show it. */
export interface ScreenplayView { screenplay: { beats: { beatId: string; index: number; words: string }[] } | null; cmsId: string | null }

export const REASONS_NOT_READABLE = "The reasons aren't readable yet: each check's status and grade are shown.";
const STATUS_WORDS: Record<string, string> = { succeeded: "passed", skipped: "skipped", failed: "failed", running: "running", ready: "not run yet", blocked: "not run yet" };
/** Pure: "script verify — passed (grade 0.91)"; a per-beat check names its beat: "screenplay readiness, beat b2 — failed". */
export const checkLine = (c: GateCheckView) =>
  `${c.stepId.replace(/_/g, " ")}${c.beatId ? `, beat ${c.beatId}` : ""} — ${STATUS_WORDS[c.status] ?? c.status}${c.score !== null ? ` (grade ${c.score})` : ""}${c.advisory ? " · advisory: never the gate's threshold" : ""}`;

/** (tf3-37; audit 10-20 B1) Pure: the gate's note as lines, one per " · " part (the engine's own separator); none without
 *  a note. The engine joins the staging line's own scores with the same " · " (workers stagingGateScores: "Staging
 *  (advisory): placement … · legibility … · collision … · density …", or "… · not graded (…)"), so a part that is one of
 *  those scores stays on the staging line: the whole line is advisory. */
const STAGING_SCORE = /^(legibility|collision|density|not graded)\b/;
export function noteLines(note: string | null): string[] {
  const out: string[] = [];
  for (const part of (note ?? "").split(" · ").map((x) => x.trim()).filter(Boolean)) {
    const last = out.length - 1;
    if (last >= 0 && out[last]!.startsWith("Staging (advisory):") && STAGING_SCORE.test(part)) out[last] += ` · ${part}`;
    else out.push(part);
  }
  return out;
}

/** Pure: the screenplay's lines in order, or what to do when there is no engine read for it yet. */
export function screenplayLines(s: ScreenplayView): { kind: "lines"; lines: string[] } | { kind: "missing"; text: string } {
  const beats = s.screenplay?.beats ?? null;
  if (beats?.length) return { kind: "lines", lines: beats.map((b) => `${b.beatId}: ${b.words.trim() || "(no words)"}`) };
  return { kind: "missing", text: `Read the screenplay in the CMS first${s.cmsId ? ` (document ${s.cmsId})` : ""}: the engine can't show it here yet. The decision still works.` };
}
